import { Injectable, inject } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Human } from '../models/data.models';
import { includesEveryNameToken, nameSearchTokens } from '../models/name-search';

@Injectable({
    providedIn: 'root'
})
export class HumanService {
    private supabase = inject(SupabaseService);

    private mapHuman(data: any): Human {
        return {
            id: data.id,
            name: data.name,
            age: data.age,
            gender: data.gender,
            club: data.club?.name ?? data.club,
            nationality: data.nationality,
            imageUrl: data.image_url,
            coverImageUrl: data.cover_image_url
        };
    }

    async getHuman(id: string): Promise<Human | null> {
        const { data, error } = await this.supabase.client
            .from('humans')
            .select('*, club:clubs(name)')
            .eq('id', id)
            .maybeSingle();

        if (error) {
            console.error('Error fetching human:', error);
            return null;
        }

        return data ? this.mapHuman(data) : null;
    }

    async getAllHumans(): Promise<Human[]> {
        const { data, error } = await this.supabase.client
            .from('humans')
            .select('*, club:clubs(name)');

        if (error) {
            console.error('Error fetching all humans:', error);
            return [];
        }

        return (data || []).map(h => this.mapHuman(h));
    }

    async getHumansByClubName(clubName: string): Promise<Human[]> {
        const { data, error } = await this.supabase.client.rpc('get_club_members', {
            p_club_name: clubName,
        });
        if (error) throw error;
        return (data || []).map((human: any) => this.mapHuman({
            id: human.human_id,
            name: human.human_name,
            age: human.human_age,
            gender: human.human_gender,
            nationality: human.human_nationality,
            image_url: human.human_image_url,
        }));
    }

    getCategory(human: Human): string {
        const age = human.age;
        const genderCode = human.gender === 'Female' ? 'F' : 'H';

        if (age <= 10) return genderCode + 'BB'; // Baby
        if (age <= 12) return genderCode + 'EA'; // Enfant
        if (age <= 14) return genderCode + 'B';  // Benjamin
        if (age <= 16) return genderCode + 'M';  // Minime
        if (age <= 18) return genderCode + 'C';  // Cadet
        if (age <= 20) return genderCode + 'J';  // Junior
        if (age <= 39) return genderCode + 'S';  // Sénior
        if (age <= 49) return genderCode + 'V1';
        if (age <= 59) return genderCode + 'V2';
        if (age >= 60) return genderCode + 'V3';

        return genderCode + 'S';
    }

    async updateHuman(id: string, human: Partial<Human>): Promise<void> {
        const updates: Record<string, unknown> = {};
        if (human.name !== undefined) updates['name'] = human.name;
        if (human.age !== undefined) updates['age'] = human.age;
        if (human.gender !== undefined) updates['gender'] = human.gender;
        if (human.nationality !== undefined) updates['nationality'] = human.nationality;
        if (human.imageUrl !== undefined) updates['image_url'] = human.imageUrl;
        if (human.coverImageUrl !== undefined) updates['cover_image_url'] = human.coverImageUrl;
        if (Object.keys(updates).length === 0) return;
        const { error } = await this.supabase.client
            .from('humans')
            .update(updates)
            .eq('id', id);

        if (error) throw error;
    }

    async updateHumanClub(id: string, clubId: string | null): Promise<void> {
        const { error } = await this.supabase.client.from('humans').update({ club_id: clubId }).eq('id', id);
        if (error) throw error;
    }

    async findHumanByProfileId(profileId: string): Promise<Human | null> {
        // Assuming there's a profile_id column on humans, or we match by some other logic.
        // For now, let's try to match by name if we can, OR handle the case where Human ID = Profile ID 
        // in some systems. Let's assume there is a profile_id column now as it's best practice.
        const { data, error } = await this.supabase.client
            .from('humans')
            .select('*, club:clubs(name)')
            .eq('profile_id', profileId)
            .maybeSingle();

        if (error) {
            console.error('Error finding human by profile:', error);
            return null;
        }

        if (!data) return null;

        return this.mapHuman(data);
    }

    getFlagEmoji(nationality: string | undefined): string {
        switch (nationality) {
            case 'FRA': return '🇫🇷';
            case 'BEL': return '🇧🇪';
            case 'ENG': return '🇬🇧';
            default: return '🏳️';
        }
    }

    async linkHumanToProfile(humanId: string, profileId: string): Promise<void> {
        const { error } = await this.supabase.client
            .from('humans')
            .update({ profile_id: profileId })
            .eq('id', humanId);

        if (error) {
            console.error('Error linking human to profile:', error);
            throw error;
        }
    }

    async searchUnclaimedHumans(query: string, excludeHumanId: string): Promise<Human[]> {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < 3) return [];
        const tokens = nameSearchTokens(normalizedQuery, 3);
        if (!tokens.length) return [];

        const anchorToken = tokens.reduce((longest, token) => token.length > longest.length ? token : longest, '');
        const humansQuery = this.supabase.client
            .from('humans')
            .select('*, club:clubs(name)')
            .is('profile_id', null)
            .neq('id', excludeHumanId)
            .ilike('name', `%${anchorToken}%`);
        const { data, error } = await humansQuery.limit(50);

        if (error) {
            console.error('Error searching merge candidates:', error);
            throw error;
        }

        return (data || []).filter(human => includesEveryNameToken(human.name, normalizedQuery))
            .slice(0, 12).map(human => this.mapHuman(human));
    }

    async searchAdminMergeCandidates(query: string, excludeHumanId: string): Promise<Human[]> {
        return this.searchUnclaimedHumans(query, excludeHumanId);
    }

    async mergeHumanIntoMyProfile(sourceHumanId: string, targetHumanId: string): Promise<string> {
        const { data, error } = await this.supabase.client.rpc('merge_my_human_profile', {
            p_source_human_id: sourceHumanId,
            p_target_human_id: targetHumanId,
        });

        if (error) {
            console.error('Error merging human profiles:', error);
            throw error;
        }

        return String(data);
    }

    async mergeHumanProfilesAsAdmin(sourceHumanId: string, targetHumanId: string): Promise<string> {
        const { data, error } = await this.supabase.client.rpc('merge_human_profiles_as_admin', {
            p_source_human_id: sourceHumanId,
            p_target_human_id: targetHumanId,
        });

        if (error) {
            console.error('Error merging human profiles as admin:', error);
            throw error;
        }

        return String(data);
    }
}
